import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, DollarSign } from "lucide-react";
import { TIPOS_MODALIDAD } from "@/lib/constants";

const IDIOMA_LABEL = { es: "Español", en: "Inglés" };
const GENERAL_LABEL = "Todos los idiomas (general)";

export default function TarifaTourDialog({ open, onOpenChange, tour, onSaved }) {
  const [precios, setPrecios] = useState({}); // key: tipo|idioma|variante -> {a,n,i}
  const [moneda, setMoneda] = useState("USD");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !tour) return;
    setLoading(true);
    api.get(`/tours/${tour.id}/tarifas`)
      .then(({ data }) => {
        const map = {};
        (data.tarifas || []).forEach((x) => {
          map[`${x.modalidad}|${x.idioma || ""}|${x.variante || ""}`] = {
            a: x.precio_adulto || 0, n: x.precio_nino || 0, i: x.precio_infante || 0,
          };
        });
        setPrecios(map);
        setMoneda(data.moneda || "USD");
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [open, tour]);

  if (!tour) return null;

  const tipos = [...new Set((tour.modalidades || []).map((m) => m.tipo))];
  // Para tours con mas de 1 idioma se ofrecen las filas por idioma especifico Y una fila
  // "general" (idioma=null): la que usa el importador cuando la columna Idioma viene vacia
  // y la que usa el Portal como fallback si no hay una tarifa exacta por idioma.
  const idiomas = (tour.idiomas || []).length > 1 ? [...tour.idiomas, null] : [null];
  // Variante SIN/CON BARCO (ej. Cataratas del Niágara): cada tour con esta variante
  // necesita una fila de precio propia por cada una -- si no, al guardar se perdería
  // el split (el PUT reemplaza TODAS las tarifas del tour).
  const variantes = tour.requiere_variante_barco ? ["sin_barco", "con_barco"] : [null];
  const filas = [];
  tipos.forEach((tipo) => idiomas.forEach((idioma) => variantes.forEach((variante) => filas.push({ tipo, idioma, variante }))));

  const setPrecio = (key, campo, val) => {
    setPrecios((p) => ({ ...p, [key]: { ...(p[key] || { a: 0, n: 0, i: 0 }), [campo]: val } }));
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const tarifas = [];
      Object.entries(precios).forEach(([key, v]) => {
        const [modalidad, idioma, variante] = key.split("|");
        const a = Number(v.a || 0), n = Number(v.n || 0), iv = Number(v.i || 0);
        if (a || n || iv) tarifas.push({ modalidad, idioma: idioma || null, variante: variante || null, precio_adulto: a, precio_nino: n, precio_infante: iv });
      });
      await api.put(`/tours/${tour.id}/tarifas`, { moneda, tarifas });
      toast.success("Tarifas base del tour guardadas.");
      onOpenChange(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tarifa-tour-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <DollarSign className="h-5 w-5" /> Tarifas base · {tour.nombre}
          </DialogTitle>
          <DialogDescription>
            Precio por defecto (adulto / niño / infante) por modalidad{idiomas.length > 1 ? " e idioma" : ""}
            {tour.requiere_variante_barco ? " y variante (sin/con barco)" : ""}.
            El Portal de Agencias usa esta tarifa cuando la agencia NO tiene una propia cargada.
            {idiomas.length > 1 && " La fila \"Todos los idiomas (general)\" se usa como respaldo si no hay una tarifa específica para ese idioma (por ejemplo, la que carga la importación de archivo cuando la columna Idioma viene vacía)."}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 inline" /></div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5 max-w-[140px]">
              <Label className="text-xs">Moneda</Label>
              <Input value={moneda} onChange={(e) => setMoneda(e.target.value.toUpperCase())} maxLength={3} data-testid="tarifa-tour-moneda" />
            </div>

            <div className="rounded-md border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    {["Modalidad", ...(idiomas.length > 1 ? ["Idioma"] : []), ...(tour.requiere_variante_barco ? ["Variante"] : []), "Adulto", "Niño", "Infante"].map((h, i) => (
                      <TableHead key={i} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filas.map(({ tipo, idioma, variante }) => {
                    const key = `${tipo}|${idioma || ""}|${variante || ""}`;
                    const v = precios[key] || { a: "", n: "", i: "" };
                    return (
                      <TableRow key={key} data-testid={`tarifa-tour-row-${key}`}>
                        <TableCell className="text-sm text-slate-700">{TIPOS_MODALIDAD[tipo] || tipo}</TableCell>
                        {idiomas.length > 1 && (
                          <TableCell className="text-sm text-slate-600">
                            {idioma ? (IDIOMA_LABEL[idioma] || idioma) : (
                              <span className="text-slate-400 italic">{GENERAL_LABEL}</span>
                            )}
                          </TableCell>
                        )}
                        {tour.requiere_variante_barco && (
                          <TableCell className="text-sm text-slate-600 font-medium">
                            {variante === "con_barco" ? "Con barco" : "Sin barco"}
                          </TableCell>
                        )}
                        {["a", "n", "i"].map((c) => (
                          <TableCell key={c}>
                            <Input
                              type="number" min={0} className="h-8 w-24"
                              value={v[c] ?? ""}
                              onChange={(e) => setPrecio(key, c, e.target.value)}
                              data-testid={`tarifa-tour-${c}-${key}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <p className="text-[11px] text-slate-400">Dejá una fila en 0/vacío si esa modalidad no aplica o todavía no tiene precio definido.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tarifa-tour-cancel">Cancelar</Button>
          <Button onClick={guardar} disabled={saving || loading} data-testid="tarifa-tour-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar tarifas"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
